import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { Assumption, isFresh, ProposalLines, ProposalSum } from "@/components/canvas/kalkyle/Forslag";

const btnPrimary = "inline-flex h-9 items-center rounded-full bg-[hsl(var(--brand))] px-3.5 text-[13.5px] font-semibold text-white transition-colors hover:bg-[hsl(var(--brand-strong))] disabled:opacity-40";
const btnQuiet = "inline-flex h-9 items-center rounded-full px-3 text-[13.5px] font-medium text-foreground/65 transition-colors hover:bg-black/[0.05] hover:text-foreground disabled:opacity-40";

/**
 * «Foreslå kalkyle for posten» – the proposal appears inside the post, where the lines will land. Legg inn is the approval;
 * Forkast leaves nothing behind. A post that cannot be priced gets its one concrete question instead of a guess.
 */
export function PostSuggest({ item, forslag, ops, onClose }) {
  const { forItem, analyse, apply, state, plan, scoped } = forslag;
  const [busy, setBusy] = useState(false);
  const started = useRef(false);
  const p = forItem(item.id);
  useEffect(() => { if (!p && !started.current) { started.current = true; analyse([item.id]); } }, [p, analyse, item.id]);
  const source = p ? ((isFresh(scoped) && (scoped.items || []).some((x) => x.item_id === item.id) ? scoped : plan)) : null;

  const run = async (mode) => {
    setBusy(true);
    try {
      const r = await apply(source, [item.id], mode, ops);
      if (r.skipped?.length) toast("Posten er endret siden forslaget – legg inn på nytt");
      else toast.success(mode === "avklar" ? "Avklaring opprettet" : "Forslaget er lagt inn", r.change_id ? { action: { label: "Angre", onClick: () => ops.undo(r.change_id).catch(() => {}) } } : undefined);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!p) {
    return (
      <div className="mt-3 rounded-[14px] bg-[hsl(210_20%_98%)] px-4 py-3.5 text-[14px] text-muted-foreground" data-testid={`suggest-${item.ref}`} data-state={state}>
        {state === "error" ? <>Fikk ikke lest posten. <button type="button" onClick={() => analyse([item.id])} className="font-medium text-[hsl(var(--brand-strong))] hover:underline" data-testid="suggest-retry">Prøv igjen</button></> : "Leser posten mot prislisten …"}
      </div>
    );
  }
  const missing = p.level === "mangler";
  return (
    <div className="mt-3 rounded-[14px] bg-[hsl(210_20%_98%)] px-4 py-4" data-testid={`suggest-${item.ref}`} data-level={p.level} data-state="done">
      <div className="text-[12px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{missing ? "Kan ikke prises ennå" : "Forslag"}</div>
      {missing ? (
        <>
          <p className="mt-2 text-[15px] leading-[1.55] text-foreground">{p.question || p.why}</p>
          {p.lines?.length ? <ProposalLines lines={p.lines} className="mt-3" /> : null}
          {p.why && p.question ? <p className="mt-2 text-[13.5px] text-muted-foreground">{p.why}</p> : null}
        </>
      ) : (
        <>
          <ProposalLines lines={p.lines} className="mt-2.5" />
          <div className="mt-3"><ProposalSum p={p} testId={`suggest-sum-${item.ref}`} /></div>
          <Assumption p={p} className="mt-2.5" />
        </>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {p.applied ? <span className="text-[13.5px] text-[hsl(var(--v-ok))]">{p.applied.mode === "avklar" ? "Avklaring opprettet" : "Lagt inn"}</span> : missing ? (
          p.question && !p.manual ? <button type="button" disabled={busy} onClick={() => run("avklar")} className={btnPrimary} data-testid={`suggest-ask-${item.ref}`}>Opprett avklaring</button> : null
        ) : <button type="button" disabled={busy} onClick={() => run("legg_inn")} className={btnPrimary} data-testid={`suggest-apply-${item.ref}`}>Legg inn</button>}
        <button type="button" onClick={onClose} className={btnQuiet} data-testid={`suggest-dismiss-${item.ref}`}>{p.applied ? "Lukk" : "Forkast"}</button>
      </div>
    </div>
  );
}
